import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Star, BookmarkSimple, Clock, Storefront } from "@phosphor-icons/react";
import ImgFallback from "@/components/ImgFallback";

export default function LookDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [studio, setStudio] = useState(null);
  const [image, setImage] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const dash = id.lastIndexOf("-");
    const sid = id.slice(0, dash);
    const idx = parseInt(id.slice(dash + 1), 10) || 0;
    api.get(`/studios/${sid}`).then((r) => {
      setStudio(r.data);
      setImage(r.data.portfolio?.[idx] || r.data.hero_look || r.data.cover_image);
    });
  }, [id]);

  if (!studio) return <div className="max-w-4xl mx-auto px-5 py-10 text-cream/50" data-testid="look-loading">Loading…</div>;

  const primary = studio.services?.[0];
  const hasAvailability = (studio.availability || []).length > 0;

  return (
    <div className="max-w-4xl mx-auto pb-24" data-testid="look-detail-page">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 px-5 pt-6 text-cream/70 hover:text-gold" data-testid="look-back">
        <ArrowLeft size={16} /> <span className="text-xs uppercase tracking-[0.2em]">Back</span>
      </button>

      <div className="mt-4 relative">
        <ImgFallback src={image} alt={`${studio.name} — look`} className="w-full h-[75vh] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
      </div>

      <div className="px-5 mt-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="overline mb-1">The Look</p>
            <h1 className="font-display text-4xl md:text-5xl tracking-tighter text-cream">{primary?.name || "Signature"}</h1>
            <p className="text-cream/70 mt-2 text-sm">
              At <Link to={`/studio/${studio.id}`} className="text-gold hover:underline" data-testid="look-view-studio">{studio.name}</Link> · By {studio.professional_name}
            </p>
            <p className="mt-1 text-xs text-cream/50 flex items-center flex-wrap gap-1">
              <MapPin size={12} weight="fill" color="#C5A059" /> {studio.area}, {studio.city} · {studio.distance_km} km
              <span className="divider-dot" />
              <Star size={12} weight="fill" color="#C5A059" /> {studio.rating}
              <span className="divider-dot" />
              {studio.completed_cuts}+ verified cuts
            </p>
          </div>
          <button
            onClick={() => { setSaved(!saved); toast.success(saved ? "Look removed" : "Look saved"); }}
            className="btn-ghost px-4 py-2 rounded-full flex items-center gap-2 whitespace-nowrap"
            data-testid="save-look"
          >
            <BookmarkSimple size={16} weight={saved ? "fill" : "regular"} color={saved ? "#C5A059" : "#FAF9F6"} />
            <span className="text-xs uppercase tracking-[0.2em]">{saved ? "Saved" : "Save"}</span>
          </button>
        </div>

        <div className="mt-8 border border-cream/10 p-6">
          <p className="overline mb-3">Book this look</p>
          {primary && (
            <div className="flex items-start justify-between gap-4 py-2 border-b border-cream/10">
              <div>
                <p className="font-display text-lg tracking-tight">{primary.name}</p>
                <p className="text-xs text-cream/60 flex items-center gap-1"><Clock size={12} /> {primary.duration_minutes} min · {primary.description}</p>
              </div>
              <p className="font-display text-2xl text-gold whitespace-nowrap">${primary.price}</p>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-3 items-center justify-between">
            {hasAvailability ? (
              <p className="text-xs text-gold flex items-center gap-1">
                <Clock size={12} weight="fill" /> Next available · {new Date(studio.availability[0]).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })}
              </p>
            ) : (
              <p className="text-xs text-cream/60">No published openings</p>
            )}
            <div className="flex gap-2">
              <Link to={`/studio/${studio.id}`} data-testid="link-view-studio" className="btn-ghost px-4 py-3 rounded-full text-[10px] uppercase tracking-[0.25em] font-semibold inline-flex items-center gap-2">
                <Storefront size={14} /> View Studio
              </Link>
              <button
                data-testid="book-this-look"
                onClick={() => nav(`/booking/new/${studio.id}?service=${primary?.id || ""}&look=${id}`)}
                className="btn-gold px-6 py-3 rounded-full text-[10px] uppercase tracking-[0.25em] font-semibold"
              >
                Book this look
              </button>
            </div>
          </div>

          {!hasAvailability && (
            <div className="mt-6 border-t border-cream/10 pt-5">
              <p className="text-xs uppercase tracking-[0.2em] text-cream/60 mb-3">Or explore similar nearby</p>
              <Link to="/explore" data-testid="explore-similar" className="chip hover:border-gold">Similar Studios near you</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
